import { Component, type ReactNode } from "react";
import { QRCodeSVG } from "qrcode.react";
import { diagnostics, type QRPartsResult } from "@/lib/diagnostics";

// アプリ全体の安全網。描画クラッシュ時にフォールバック画面へ診断QRを自動表示する
// (オフライン機器では画面が唯一のエラー報告経路)。収集レイヤ自体の例外は
// diagnostics 側の防御的try-catchで握るので、ここに来るのは本体UIの障害のみ。
// 無操作が続いたら子ツリーを再マウントして自動復帰する (e区画はメモリ上にしか無いため reload しない)。

const RECOVERY_IDLE_MS = 10 * 60 * 1000;
/** 1ページロードあたりの上限。恒常的に落ちる場合にエラー記録を埋めないため */
const RECOVERY_MAX_ATTEMPTS = 3;
const RECOVERY_RESET_EVENTS = ["pointerdown", "keydown"] as const;

interface Props {
  children: ReactNode;
}

interface State {
  failed: boolean;
  qr: QRPartsResult | null;
}

export default class AppErrorBoundary extends Component<Props, State> {
  state: State = { failed: false, qr: null };
  private recoveryAttempts = 0;
  private recoveryTimer: ReturnType<typeof setTimeout> | null = null;

  static getDerivedStateFromError(): Partial<State> {
    return { failed: true };
  }

  componentDidCatch(error: unknown): void {
    diagnostics.recordError(error, "render_crash");
    // recordError 反映後のスナップショットでQRを固定する。
    try {
      this.setState({ qr: diagnostics.getQRParts() });
    } catch {
      // QR生成すら失敗した場合はメッセージのみ表示。
    }
    this.startRecoveryWatch();
  }

  componentWillUnmount(): void {
    this.stopRecoveryWatch();
  }

  private startRecoveryWatch(): void {
    this.stopRecoveryWatch();
    if (this.recoveryAttempts >= RECOVERY_MAX_ATTEMPTS) return;
    RECOVERY_RESET_EVENTS.forEach((event) =>
      window.addEventListener(event, this.scheduleRecovery, { capture: true, passive: true }),
    );
    this.scheduleRecovery();
  }

  private stopRecoveryWatch(): void {
    if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
    this.recoveryTimer = null;
    RECOVERY_RESET_EVENTS.forEach((event) =>
      window.removeEventListener(event, this.scheduleRecovery, { capture: true }),
    );
  }

  private scheduleRecovery = (): void => {
    if (this.recoveryTimer) clearTimeout(this.recoveryTimer);
    this.recoveryTimer = setTimeout(this.recover, RECOVERY_IDLE_MS);
  };

  private recover = (): void => {
    this.stopRecoveryWatch();
    this.recoveryAttempts += 1;
    diagnostics.recordEvent("render_recover", `${this.recoveryAttempts}/${RECOVERY_MAX_ATTEMPTS}`);
    this.setState({ failed: false, qr: null });
  };

  render() {
    if (!this.state.failed) return this.props.children;
    const qr = this.state.qr;
    return (
      <div className="flex h-dvh flex-col items-center justify-center gap-6 bg-white p-8 text-center">
        <div>
          <p className="text-2xl font-bold text-gray-800">画面の表示に問題が発生しました</p>
          <p className="mt-2 text-gray-600">お手数ですが、係員にお知らせください。</p>
        </div>
        {qr && (
          <div className="flex flex-col items-center gap-2">
            <div className="flex flex-wrap items-start justify-center gap-3">
              {qr.parts.map((part, i) => (
                <div key={i} className="flex flex-col items-center gap-1">
                  <QRCodeSVG
                    value={part}
                    size={qr.partCount > 1 ? 180 : 240}
                    level="M"
                    marginSize={4}
                    title={`保守用診断コード ${i + 1}/${qr.partCount}`}
                  />
                  {qr.partCount > 1 && (
                    <span className="font-mono text-xs font-bold text-gray-600">
                      {i + 1}/{qr.partCount}
                    </span>
                  )}
                </div>
              ))}
            </div>
            <p className="font-mono text-xs text-gray-400">保守用診断コード ({qr.totalBytes} B)</p>
          </div>
        )}
      </div>
    );
  }
}
